/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PrimaryButton } from '../../../../../styles/ui/Button';
import { FormGrid } from '../../../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../../../styles/forms/Field';
import {
   getDepartments,
   listCostCenters,
} from '../../../../../services/hr/hrApi';
import { updatePreCadastro } from '../../../../../services/hr/recruitment/onboardingApi';
import { formatUserLabel } from '../../../../../utils/format';
import JobPositionSelect from '../../components/JobPositionSelect';
import Accordion from '../../components/Accordion';
import {
   MARITAL_STATUS_OPTIONS,
   COMPANY_ENTITY_OPTIONS,
   TRANSPORTATION_COST_TYPE_OPTIONS,
   PRE_CADASTRO_COUNTABLE_FIELDS,
   countPreCadastroFilled,
   isPreCadastroComplete,
} from '../../constants';
import { readApiError, recruitmentErrorMessage } from '../../utils/apiError';
import { InlineHint } from '../styles';

// ---------------------------------------------------------------------------
// Pré-cadastro do futuro funcionário (rascunho de admissão) — ACCORDION
// colapsado por default (redesign 2026-07-14), com meta "Rascunho · N de 14" /
// "Completo". Espelha os campos do cadastro avulso (EmployeeForm); TODOS são
// opcionais AO SALVAR — os 5 com * (+ data de início) só são exigidos no
// ADMITIR. Salvar REGENERA o checklist no backend — o onSaved do pai recarrega
// tudo. '' vira null no service (limpa o campo). `users` vem por prop (fetch
// único na página, compartilhado com o rail).
// ---------------------------------------------------------------------------

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

// Estado do form derivado do onboarding (ids numéricos → string p/ <select>).
function draftFromOnboarding(onboarding) {
   return {
      marital_status: onboarding?.marital_status ?? '',
      company_entity: onboarding?.company_entity ?? '',
      job_position_id: onboarding?.job_position_id
         ? String(onboarding.job_position_id)
         : '',
      department_id: onboarding?.department_id
         ? String(onboarding.department_id)
         : '',
      cost_center_id: onboarding?.cost_center_id
         ? String(onboarding.cost_center_id)
         : '',
      base_salary: onboarding?.base_salary ?? '',
      city_location: onboarding?.city_location ?? '',
      monthly_work_hours: onboarding?.monthly_work_hours ?? 220,
      transportation_cost_type: onboarding?.transportation_cost_type ?? '',
      has_meal_allowance: Boolean(onboarding?.has_meal_allowance),
      is_technician: Boolean(onboarding?.is_technician),
      is_salesperson: Boolean(onboarding?.is_salesperson),
      pix_key: onboarding?.pix_key ?? '',
      whatsapp_number: onboarding?.whatsapp_number ?? '',
      email: onboarding?.email ?? '',
      responsible_hr_user_id: onboarding?.responsible_hr_user_id
         ? String(onboarding.responsible_hr_user_id)
         : '',
      start_date: onboarding?.start_date?.substring(0, 10) ?? '',
   };
}

export default function PreCadastroCard({
   onboarding,
   canUpdate,
   users = [],
   onSaved,
}) {
   const [form, setForm] = useState(() => draftFromOnboarding(onboarding));
   const [fieldErrors, setFieldErrors] = useState({});
   const [isSaving, setIsSaving] = useState(false);

   const [departments, setDepartments] = useState([]);
   const [costCenters, setCostCenters] = useState([]);

   useEffect(() => {
      setForm(draftFromOnboarding(onboarding));
      setFieldErrors({});
   }, [onboarding]);

   // Selects auxiliares — 1 fetch no mount (cargos ficam com o JobPositionSelect;
   // usuários vêm por prop, compartilhados com o rail).
   useEffect(() => {
      let ignore = false;
      Promise.all([
         getDepartments().catch(() => []),
         listCostCenters().catch(() => []),
      ]).then(([departmentList, costCenterList]) => {
         if (ignore) return;
         setDepartments(departmentList);
         setCostCenters(costCenterList);
      });
      return () => {
         ignore = true;
      };
   }, []);

   const handleChange = (event) => {
      const { name, value, type, checked } = event.target;
      setForm((prev) => ({
         ...prev,
         [name]: type === 'checkbox' ? checked : value,
      }));
      setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
   };

   const handleSave = async () => {
      if (isSaving) return;

      // snake_case = nomes do backend; '' vira null no service. Ids/números
      // convertidos aqui (select devolve string).
      const payload = {
         marital_status: form.marital_status,
         company_entity: form.company_entity,
         job_position_id: form.job_position_id
            ? Number(form.job_position_id)
            : '',
         department_id: form.department_id ? Number(form.department_id) : '',
         cost_center_id: form.cost_center_id ? Number(form.cost_center_id) : '',
         base_salary: form.base_salary !== '' ? Number(form.base_salary) : '',
         city_location: form.city_location,
         monthly_work_hours:
            form.monthly_work_hours !== '' ? Number(form.monthly_work_hours) : '',
         transportation_cost_type: form.transportation_cost_type,
         has_meal_allowance: form.has_meal_allowance,
         is_technician: form.is_technician,
         is_salesperson: form.is_salesperson,
         pix_key: form.pix_key,
         whatsapp_number: form.whatsapp_number,
         email: form.email,
         responsible_hr_user_id: form.responsible_hr_user_id
            ? Number(form.responsible_hr_user_id)
            : '',
         start_date: form.start_date,
      };

      setIsSaving(true);
      setFieldErrors({});
      try {
         const { message } = await updatePreCadastro(onboarding.id, payload);
         toast.success(message);
         onSaved?.();
      } catch (err) {
         const { fieldErrors: apiFieldErrors } = readApiError(err);
         if (Object.keys(apiFieldErrors).length) {
            setFieldErrors(apiFieldErrors);
            toast.error('Corrija os campos destacados.');
         } else {
            toast.error(
               recruitmentErrorMessage(err, 'Erro ao salvar o pré-cadastro.'),
            );
         }
      } finally {
         setIsSaving(false);
      }
   };

   const readOnly = !canUpdate;
   const renderError = (key) =>
      fieldErrors[key] ? <FieldError>{fieldErrors[key]}</FieldError> : null;

   const accordionMeta = isPreCadastroComplete(onboarding)
      ? 'Completo'
      : `Rascunho · ${countPreCadastroFilled(onboarding)} de ${
           PRE_CADASTRO_COUNTABLE_FIELDS.length
        } campos preenchidos`;

   return (
      <Accordion title="Pré-cadastro do funcionário" meta={accordionMeta}>
         <InlineHint style={{ margin: '16px 0 12px' }}>
            Rascunho da admissão — os campos com * (e a data de início) são
            exigidos apenas na hora de admitir. Estado civil e cargo alteram o
            checklist de documentos.
         </InlineHint>

         <FormGrid>
            <Field className="col-4">
               <label htmlFor="pre-marital-status">Estado civil</label>
               <select
                  id="pre-marital-status"
                  name="marital_status"
                  value={form.marital_status}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.marital_status || undefined}
               >
                  <option value="">Selecione…</option>
                  {MARITAL_STATUS_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
               {renderError('marital_status')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-company-entity">
                  Empresa
                  <RequiredMark>*</RequiredMark>
               </label>
               <select
                  id="pre-company-entity"
                  name="company_entity"
                  value={form.company_entity}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.company_entity || undefined}
               >
                  <option value="">Selecione…</option>
                  {COMPANY_ENTITY_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
               {renderError('company_entity')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-start-date">Data de início</label>
               <input
                  id="pre-start-date"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.start_date || undefined}
               />
               {renderError('start_date')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-job-position">
                  Cargo (efetivo)
                  <RequiredMark>*</RequiredMark>
               </label>
               <JobPositionSelect
                  id="pre-job-position"
                  value={form.job_position_id}
                  onChange={(value) => {
                     setForm((prev) => ({ ...prev, job_position_id: value }));
                     setFieldErrors((prev) => ({
                        ...prev,
                        job_position_id: undefined,
                     }));
                  }}
                  disabled={readOnly || isSaving}
                  invalid={!!fieldErrors.job_position_id}
               />
               {renderError('job_position_id')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-department">
                  Departamento
                  <RequiredMark>*</RequiredMark>
               </label>
               <select
                  id="pre-department"
                  name="department_id"
                  value={form.department_id}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.department_id || undefined}
               >
                  <option value="">Selecione…</option>
                  {departments.map((department) => (
                     <option key={department.id} value={String(department.id)}>
                        {department.department_name ?? department.name}
                     </option>
                  ))}
               </select>
               {renderError('department_id')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-cost-center">
                  Centro de custo
                  <RequiredMark>*</RequiredMark>
               </label>
               <select
                  id="pre-cost-center"
                  name="cost_center_id"
                  value={form.cost_center_id}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.cost_center_id || undefined}
               >
                  <option value="">Selecione…</option>
                  {costCenters.map((costCenter) => (
                     <option key={costCenter.id} value={String(costCenter.id)}>
                        {costCenter.cost_center_code} —{' '}
                        {costCenter.cost_center_name}
                     </option>
                  ))}
               </select>
               {renderError('cost_center_id')}
            </Field>

            <Field className="col-3">
               <label htmlFor="pre-base-salary">
                  Salário base (R$)
                  <RequiredMark>*</RequiredMark>
               </label>
               <input
                  id="pre-base-salary"
                  type="number"
                  name="base_salary"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={form.base_salary}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.base_salary || undefined}
               />
               {renderError('base_salary')}
            </Field>

            <Field className="col-3">
               <label htmlFor="pre-monthly-hours">Carga horária mensal (h)</label>
               <input
                  id="pre-monthly-hours"
                  type="number"
                  name="monthly_work_hours"
                  min="1"
                  value={form.monthly_work_hours}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.monthly_work_hours || undefined}
               />
               {renderError('monthly_work_hours')}
            </Field>

            <Field className="col-3">
               <label htmlFor="pre-transportation">Tipo de transporte</label>
               <select
                  id="pre-transportation"
                  name="transportation_cost_type"
                  value={form.transportation_cost_type}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
               >
                  <option value="">Selecione…</option>
                  {TRANSPORTATION_COST_TYPE_OPTIONS.map((option) => (
                     <option key={option.value} value={option.value}>
                        {option.label}
                     </option>
                  ))}
               </select>
            </Field>

            <Field className="col-3">
               <label htmlFor="pre-city-location">Cidade de atuação</label>
               <input
                  id="pre-city-location"
                  type="text"
                  name="city_location"
                  placeholder="Ex: Goiânia"
                  value={form.city_location}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
               />
            </Field>

            <Field className="col-4">
               <label
                  htmlFor="pre-has-meal-allowance"
                  style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px',
                     cursor: readOnly ? 'default' : 'pointer',
                  }}
               >
                  <input
                     id="pre-has-meal-allowance"
                     type="checkbox"
                     name="has_meal_allowance"
                     checked={form.has_meal_allowance}
                     onChange={handleChange}
                     disabled={readOnly || isSaving}
                  />
                  Recebe vale alimentação
               </label>
            </Field>

            <Field className="col-4">
               <label
                  htmlFor="pre-is-technician"
                  style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px',
                     cursor: readOnly ? 'default' : 'pointer',
                  }}
               >
                  <input
                     id="pre-is-technician"
                     type="checkbox"
                     name="is_technician"
                     checked={form.is_technician}
                     onChange={handleChange}
                     disabled={readOnly || isSaving}
                  />
                  É técnico de campo
               </label>
            </Field>

            <Field className="col-4">
               <label
                  htmlFor="pre-is-salesperson"
                  style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px',
                     cursor: readOnly ? 'default' : 'pointer',
                  }}
               >
                  <input
                     id="pre-is-salesperson"
                     type="checkbox"
                     name="is_salesperson"
                     checked={form.is_salesperson}
                     onChange={handleChange}
                     disabled={readOnly || isSaving}
                  />
                  É vendedor
               </label>
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-pix-key">Chave Pix</label>
               <input
                  id="pre-pix-key"
                  type="text"
                  name="pix_key"
                  value={form.pix_key}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.pix_key || undefined}
               />
               {renderError('pix_key')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-whatsapp">WhatsApp</label>
               <input
                  id="pre-whatsapp"
                  type="text"
                  name="whatsapp_number"
                  placeholder="(62) 90000-0000"
                  value={form.whatsapp_number}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.whatsapp_number || undefined}
               />
               {renderError('whatsapp_number')}
            </Field>

            <Field className="col-4">
               <label htmlFor="pre-email">E-mail</label>
               <input
                  id="pre-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
                  aria-invalid={!!fieldErrors.email || undefined}
               />
               {renderError('email')}
            </Field>

            <Field className="col-6">
               <label htmlFor="pre-responsible-hr">Responsável do RH</label>
               <select
                  id="pre-responsible-hr"
                  name="responsible_hr_user_id"
                  value={form.responsible_hr_user_id}
                  onChange={handleChange}
                  disabled={readOnly || isSaving}
               >
                  <option value="">Selecione…</option>
                  {users.map((user) => (
                     <option key={user.id} value={String(user.id)}>
                        {formatUserLabel(user)}
                     </option>
                  ))}
               </select>
            </Field>
         </FormGrid>

         {canUpdate && (
            <div
               style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: 14,
               }}
            >
               <PrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
               >
                  {isSaving ? 'Salvando…' : 'Salvar pré-cadastro'}
               </PrimaryButton>
            </div>
         )}
      </Accordion>
   );
}

PreCadastroCard.propTypes = {
   onboarding: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   }),
   canUpdate: PropTypes.bool,
   users: PropTypes.arrayOf(PropTypes.shape({})),
   onSaved: PropTypes.func,
};
